import { createSelector } from 'reselect';

import type { ContactActivity } from '../typescript-types/contact/contact-activity';

import { RequestState, StateDictionary } from './utils/state-manager';

type FullApplicationDictionary = { [jobApplicantId: number]: FullApplicant };
type JobApplicantDetailDictionary = { [jobApplicantId: number]: JobApplicantDetails };
type JobApplicantLabelDictionary = { [jobApplicantId: number]: JobApplicantLabelResponse };
type ContactActivityDictionary = { [jobApplicantId: number]: Array<ContactActivity> };
type ApplicationCountJobIdDictionary = { [id: number]: { [appStatus: string]: number } };
type ApplicantCountOrgIdDictionary = {
  [id: number]: Array<{ jobStatus: JobStatus; appStatus: string; count: number }>;
};
export type ApplicantFinalizingHireStepDictionary = {
  [jobApplicantId: number]: ApplicantFinalizingHireStepDetails[];
};

export type ApplicationState = {
  statusChangeReasons: Array<StatusChangeReason> | null;
  statusChangeReasonsStatus: RequestState | null;

  applicationsById: FullApplicationDictionary;
  applicationsStateById: StateDictionary;
  applicationsStateByJobIdAndStatus: { [jobIdStatus: string]: RequestState };
  applicationsStateByOrgIdAndStatus: { [orgIdStatus: string]: RequestState };
  applicationsStateByAccountIdAndStatus: { [accountIdStatus: string]: RequestState };

  applicationActivityById: ContactActivityDictionary;
  applicationActivityByIdStatus: StateDictionary;

  applicationDetailsById: JobApplicantDetailDictionary;
  applicationDetailsStateById: StateDictionary;

  labelsByJobApplicantId: JobApplicantLabelDictionary;
  labelStatusByJobId: StateDictionary;

  applicationCountsByJobId: ApplicationCountJobIdDictionary;
  applicationCountsByOrgId: ApplicantCountOrgIdDictionary;
  applicationCountsStateByJobId: StateDictionary;
  applicationCountsStateByOrgId: StateDictionary;

  applicantFinalizingHireStepsByJobApplicantId: ApplicantFinalizingHireStepDictionary;
  applicantFinalizingHireStepsStateByJobId: StateDictionary;
  applicantFinalizingHireStepsStateByOrgId: StateDictionary;
};
type State = { applications: ApplicationState };

export const statusChangeReasonsSelector = (state: State) => state.applications.statusChangeReasons;
export const statusChangeReasonsStateSelector = (state: State) =>
  state.applications.statusChangeReasonsStatus;

export const applicationsStateByIdSelector = (state: State) =>
  state.applications.applicationsStateById;
export const applicationsStateForIdSelector = createSelector(
  [applicationsStateByIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (applicationsStateById, jobApplicantId) => applicationsStateById[jobApplicantId],
);

export const applicationsStateByJobIdAndStatusSelector = (state: State) =>
  state.applications.applicationsStateByJobIdAndStatus;
export const applicationsStateForJobIdAndStatusSelector = createSelector(
  [
    applicationsStateByJobIdAndStatusSelector,
    (_state: State, jobId: number) => jobId,
    (_state: State, _jobId: number, status: ApplicationStatus) => status,
  ],
  (applicationsStateByJobIdAndStatus, jobId, status) =>
    applicationsStateByJobIdAndStatus[`${jobId}${status}`],
);

export const applicationsStateByOrgIdAndStatusSelector = (state: State) =>
  state.applications.applicationsStateByOrgIdAndStatus;
export const applicationsStateForOrgIdAndStatusSelector = createSelector(
  [
    applicationsStateByOrgIdAndStatusSelector,
    (_state: State, orgId: number) => orgId,
    (_state: State, _orgId: number, status: ApplicationStatus) => status,
  ],
  (applicationsStateByOrgIdAndStatus, orgId, status) =>
    applicationsStateByOrgIdAndStatus[`${orgId}${status}`],
);

export const applicationsStateByAccountIdAndStatusSelector = (state: State) =>
  state.applications.applicationsStateByAccountIdAndStatus;
export const applicationsStateForAccountIdAndStatusSelector = createSelector(
  [
    applicationsStateByAccountIdAndStatusSelector,
    (_state: State, accountId: number) => accountId,
    (_state: State, _accountId: number, status: ApplicationStatus) => status,
  ],
  (applicationsStateByAccountIdAndStatus, accountId, status) =>
    applicationsStateByAccountIdAndStatus[`${accountId}${status}`],
);

export const activityByJobApplicantIdStatusSelector = (state: State) =>
  state.applications.applicationActivityByIdStatus;
export const jobApplicantActivityStatusSelector = createSelector(
  [
    activityByJobApplicantIdStatusSelector,
    (_state: State, jobApplicantId: number) => jobApplicantId,
  ],
  (activityByJobApplicantIdStatus, jobApplicantId) =>
    activityByJobApplicantIdStatus ? activityByJobApplicantIdStatus[jobApplicantId] : null,
);

export const applicationDetailsByIdSelector = (state: State) =>
  state.applications.applicationDetailsById;
export const applicationDetailsForIdSelector = createSelector(
  [applicationDetailsByIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (applicationDetailsById, jobApplicantId) => applicationDetailsById[jobApplicantId],
);

export const applicationDetailsStateByIdSelector = (state: State) =>
  state.applications.applicationDetailsStateById;
export const applicationDetailsStateForIdSelector = createSelector(
  [applicationDetailsStateByIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (applicationDetailsStateById, jobApplicantId) => applicationDetailsStateById[jobApplicantId],
);

export const activityByJobApplicantIdSelector = (state: State) =>
  state.applications.applicationActivityById;
export const jobApplicantActivitySelector = createSelector(
  [activityByJobApplicantIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (activityByJobApplicantId, jobApplicantId) =>
    activityByJobApplicantId ? activityByJobApplicantId[jobApplicantId] : null,
);

export const fullApplicationsByIdSelector = (state: State) => state.applications.applicationsById;
export const fullApplicationSelector = createSelector(
  [fullApplicationsByIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (fullApplicationsById: FullApplicationDictionary, jobApplicantId: number) =>
    fullApplicationsById[jobApplicantId],
);

export const fullApplicationsForJobSelector = createSelector(
  [fullApplicationsByIdSelector, (_state: State, jobId: number) => jobId],
  (fullApplicationsById, jobId) =>
    Object.values(fullApplicationsById).filter((app) => app.job_id === jobId),
);

export const fullApplicationsForJobAndStatusSelector = createSelector(
  [
    fullApplicationsByIdSelector,
    (_state: State, jobId: number) => jobId,
    (_state: State, _jobId: number, status: ApplicationStatus) => status,
  ],
  (fullApplicationsById, jobId, status) =>
    Object.values(fullApplicationsById).filter(
      (app) => app.job_id === jobId && app.status === status,
    ),
);

export const fullApplicationsForOrgIdSelector = createSelector(
  [fullApplicationsByIdSelector, (_state: State, orgId: number) => orgId],
  (fullApplicationsById, orgId) =>
    Object.values(fullApplicationsById).filter((app) => app.orgId === orgId),
);

export const fullApplicationsForOrgIdAndStatusSelector = createSelector(
  [
    fullApplicationsByIdSelector,
    (_state: State, orgId: number) => orgId,
    (_state: State, _orgId: number, status: ApplicationStatus) => status,
  ],
  (fullApplicationsById, orgId, status) =>
    Object.values(fullApplicationsById).filter(
      (app) => app.orgId === orgId && app.status === status,
    ),
);

export const fullApplicationsForAccountIdSelector = createSelector(
  [fullApplicationsByIdSelector, (_state: State, accountId: number) => accountId],
  (fullApplicationsById, accountId) =>
    Object.values(fullApplicationsById).filter((app) => app.accountId === accountId),
);

const applicationCountsByJobIdSelector = (state: State) =>
  state.applications.applicationCountsByJobId;
export const applicationCountsForJobIdSelector = createSelector(
  [applicationCountsByJobIdSelector, (_state: State, jobId: number) => jobId],
  (applicationCountsByJobId, jobId) => applicationCountsByJobId[jobId] || {},
);
const applicationCountsByOrgIdSelector = (state: State) =>
  state.applications.applicationCountsByOrgId;
export const applicationCountsForOrgIdSelector = createSelector(
  [applicationCountsByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (applicationCountsByOrgId, orgId) => applicationCountsByOrgId[orgId] || {},
);
export const applicationCountsStateForJobIdSelector = createSelector(
  [
    (state: State) => state.applications.applicationCountsStateByJobId,
    (_state: State, jobId: number) => jobId,
  ],
  (applicationCountsStateByJobId, jobId) => applicationCountsStateByJobId[jobId] || {},
);
export const applicationCountsStateForOrgIdSelector = createSelector(
  [
    (state: State) => state.applications.applicationCountsStateByOrgId,
    (_state: State, orgId: number) => orgId,
  ],
  (applicationCountsStateByOrgId, orgId) => applicationCountsStateByOrgId[orgId] || {},
);

export const labelsByJobApplicantIdSelector = (state: State) =>
  state.applications.labelsByJobApplicantId;
export const labelsForJobApplicantIdSelector = createSelector(
  [labelsByJobApplicantIdSelector, (_state: State, jobApplicantId: number) => jobApplicantId],
  (labelsByJobApplicantId, jobApplicantId: number) =>
    labelsByJobApplicantId ? labelsByJobApplicantId[jobApplicantId] : null,
);

export const labelStateByJobIdSelector = (state: State) => state.applications.labelStatusByJobId;
export const labelStateForJobIdSelector = createSelector(
  [labelStateByJobIdSelector, (_state: State, jobId: number) => jobId],
  (labelStateByJobId, jobId: number) => labelStateByJobId[jobId],
);

const applicantFinalizingHireStepsByJobApplicantIdSelector = (state: State) =>
  state.applications.applicantFinalizingHireStepsByJobApplicantId;
export const applicantFinalizingHireStepsForJobApplicantIdSelector = createSelector(
  [
    applicantFinalizingHireStepsByJobApplicantIdSelector,
    (_state: State, jobApplicantId: number) => jobApplicantId,
  ],
  (applicantFinalizingHireStepsByJobApplicantId, jobApplicantId: number) =>
    applicantFinalizingHireStepsByJobApplicantId[jobApplicantId],
);

const applicantFinalizingHireStepsStateByJobIdSelector = (state: State) =>
  state.applications.applicantFinalizingHireStepsStateByJobId;
export const applicantFinalizingHireStepsStateForJobIdSelector = createSelector(
  [applicantFinalizingHireStepsStateByJobIdSelector, (_state: State, jobId: number) => jobId],
  (applicantFinalizingHireStepsStateByJobId, jobId: number) =>
    applicantFinalizingHireStepsStateByJobId[jobId],
);

const applicantFinalizingHireStepsStateByOrgIdSelector = (state: State) =>
  state.applications.applicantFinalizingHireStepsStateByOrgId;
export const applicantFinalizingHireStepsStateForOrgIdSelector = createSelector(
  [applicantFinalizingHireStepsStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (applicantFinalizingHireStepsStateByOrgId, orgId: number) =>
    applicantFinalizingHireStepsStateByOrgId[orgId],
);
